import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  Tooltip,
  XAxis,
  YAxis,
  Legend,
  BarChart,
  Bar
} from "recharts";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { SERVER_URL } from "../config";

const PerformanceStatsModal = ({ isOpen, onClose }) => {

  const [loading, setLoading] = useState(false);

  const [companyStats, setCompanyStats] = useState([]);
  const [branchDetails, setBranchDetails] = useState(null);
  const [selectedBranch, setSelectedBranch] = useState(null);

  const [yearlyGraph, setYearlyGraph] = useState([]);
  const [monthlyGraph, setMonthlyGraph] = useState([]);

  const currentYear = new Date().getFullYear();

  const years = [
    currentYear,
    currentYear - 1,
    currentYear - 2
  ];

  /* ---------------- FETCH COMPANY STATS ---------------- */

  const fetchCompanyStats = async () => {

    try {

      setLoading(true);

      const res = await axios.get(
        `${SERVER_URL}/performance/company?years=${years.join(",")}`
      );

      setCompanyStats(res.data.branches);
      setYearlyGraph(res.data.yearly_graph);
      setMonthlyGraph(res.data.monthly_graph);

      setLoading(false);

    } catch (err) {
      console.error("Company stats error", err);
      setLoading(false);
    }

  };

  /* ---------------- FETCH BRANCH DETAILS ---------------- */

  const fetchBranchDetails = async (branchId) => {

    try {

      setSelectedBranch(branchId);

      const res = await axios.get(
        `${SERVER_URL}/performance/branch/${branchId}?years=${years.join(",")}`
      );

      setBranchDetails(res.data);

    } catch (err) {
      console.error("Branch stats error", err);
    }

  };

  useEffect(() => {
    if (isOpen) fetchCompanyStats();
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">

      <div className="bg-white w-[95%] max-w-7xl rounded-xl shadow-xl p-6 max-h-[90vh] overflow-y-auto">

        {/* HEADER */}

        <div className="flex justify-between items-center mb-6">

          <h2 className="text-2xl font-semibold text-gray-800">
            Company Performance Stats
          </h2>

          <button onClick={onClose}>
            <XMarkIcon className="w-6 h-6 text-gray-500"/>
          </button>

        </div>

        {/* YEARLY GRAPH */}

        <div className="bg-gray-50 p-4 rounded-lg mb-8">

          <h3 className="text-lg font-semibold mb-3">
            Performance Trend (Last 3 Years)
          </h3>

          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={yearlyGraph}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="year"/>
              <YAxis/>
              <Tooltip/>
              <Legend/>
              <Line type="monotone" dataKey="open" stroke="#f59e0b"/>
              <Line type="monotone" dataKey="closed" stroke="#3b82f6"/>
              <Line type="monotone" dataKey="successful" stroke="#10b981"/>
            </LineChart>
          </ResponsiveContainer>

        </div>

        {/* MONTHLY GRAPH */}

        <div className="bg-gray-50 p-4 rounded-lg mb-8">

          <h3 className="text-lg font-semibold mb-3">
            Month Wise Performance (April → March)
          </h3>

          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={monthlyGraph}>
              <CartesianGrid strokeDasharray="3 3"/>
              <XAxis dataKey="month"/>
              <YAxis/>
              <Tooltip/>
              <Legend/>
              <Bar dataKey="open" stackId="a"/>
              <Bar dataKey="closed" stackId="a"/>
              <Bar dataKey="successful" stackId="a"/>
            </BarChart>
          </ResponsiveContainer>

        </div>

        {/* GRID SECTION */}

        <div className="grid grid-cols-2 gap-6">

          {/* BRANCH PERFORMANCE GRID */}

          <div className="bg-gray-50 rounded-lg p-4">

            <h3 className="text-lg font-semibold mb-4">
              Branch Performance
            </h3>

            <table className="w-full text-sm">

              <thead>
                <tr className="text-left border-b">
                  <th className="py-2">Branch</th>
                  <th>Open</th>
                  <th>Closed</th>
                  <th>Success</th>
                  <th>Rate %</th>
                </tr>
              </thead>

              <tbody>

                {companyStats.map((branch) => {

                  const rate = branch.success_rate || 0;

                  return (
                    <tr
                      key={branch.branch_id}
                      className="border-b cursor-pointer hover:bg-gray-100"
                      onClick={() => fetchBranchDetails(branch.branch_id)}
                    >

                      <td className="py-2 font-medium">
                        {branch.branch_name}
                      </td>

                      <td>{branch.open}</td>
                      <td>{branch.closed}</td>
                      <td>{branch.successful}</td>
                      <td className="text-green-600 font-medium">
                        {rate}%
                      </td>

                    </tr>
                  );

                })}

              </tbody>

            </table>

          </div>

          {/* EMPLOYEE + TEAM PERFORMANCE */}

          <div className="bg-gray-50 rounded-lg p-4">

            <h3 className="text-lg font-semibold mb-4">
              Branch Details
            </h3>

            {!branchDetails ? (
              <div className="text-gray-400">
                Select a branch to view details
              </div>
            ) : (

              <div>

                {/* TEAM STATS */}

                <h4 className="font-semibold mb-2">Team Performance</h4>

                <table className="w-full text-sm mb-6">

                  <thead>
                    <tr className="border-b">
                      <th className="py-2">Team</th>
                      <th>Open</th>
                      <th>Closed</th>
                      <th>Success</th>
                      <th>Rate</th>
                    </tr>
                  </thead>

                  <tbody>

                    {branchDetails.teams.map((team) => (

                      <tr key={team.team_id} className="border-b">

                        <td className="py-2">{team.team_name}</td>
                        <td>{team.open}</td>
                        <td>{team.closed}</td>
                        <td>{team.successful}</td>
                        <td className="text-green-600">
                          {team.success_rate}%
                        </td>

                      </tr>

                    ))}

                  </tbody>

                </table>

                {/* EMPLOYEE STATS */}

                <h4 className="font-semibold mb-2">Employee Performance</h4>

                <table className="w-full text-sm">

                  <thead>
                    <tr className="border-b">
                      <th className="py-2">Employee</th>
                      <th>Open</th>
                      <th>Closed</th>
                      <th>Success</th>
                      <th>Rate</th>
                    </tr>
                  </thead>

                  <tbody>

                    {branchDetails.employees.map((emp) => (

                      <tr key={emp.employee_id} className="border-b">

                        <td className="py-2">
                          {emp.employee_name}
                        </td>

                        <td>{emp.open}</td>
                        <td>{emp.closed}</td>
                        <td>{emp.successful}</td>

                        <td className="text-green-600">
                          {emp.success_rate}%
                        </td>

                      </tr>

                    ))}

                  </tbody>

                </table>

              </div>

            )}

          </div>

        </div>

      </div>

    </div>
  );
};

export default PerformanceStatsModal;